import { compact, filter, flatMap, forEach, isEmpty, keys, map, some, startCase, uniq } from 'es-toolkit/compat';

import { AutomaticCondition, ConditionOperator, ConditionType } from 'interfaces/automaticCondition';
import { Input, SpotDetectionCountBin } from 'interfaces/calculateFeatures';
import { AttributesTable } from 'interfaces/inferenceArtifacts';
import {
  CellSegmentationGeometryValue,
  cellSegmentationGeometryValues,
  getCellSegmentationGeometries,
  ModelUserPreferences,
} from 'utils/models';
import { OrchestrationMetadata } from './classConfigFromSelections';
import { getDetectionLabelOptions } from './PostProcessingActions/utils';

export const SPOT_DETECTION_RESULT_NAME = 'slide_detections_table';
export const MAX_SPOT_DETECTION_DILATION_UM = 12;
export const DEFAULT_SPOT_DETECTION_COMPARTMENTS = [
  cellSegmentationGeometryValues.nuclear,
  cellSegmentationGeometryValues.cell,
];
export const DEFAULT_SPOT_DETECTION_DILATIONS_UM = [2];
export const DEFAULT_SPOT_DETECTION_H_SCORE_THRESHOLDS = [1, 4, 10, 16];
export const DEFAULT_SPOT_DETECTION_POSITIVITY_THRESHOLD_SCORE = 1;

export type RnaCountMode = NonNullable<ModelUserPreferences['rnaCountMode']>;
type SpotDetectionGeometry = Pick<
  ModelUserPreferences,
  'segmentationCompartments' | 'segmentationDilationsUm' | 'rnaCountMode'
>;

export const getRnaCountMode = (dockerImage?: string): RnaCountMode => {
  const tag = dockerImage?.slice(dockerImage.lastIndexOf(':') + 1);
  const version = tag?.match(/^(?:git-)?v?(\d+)\.(\d+)\.(\d+)(?:[-_].*)?$/);
  if (!version) return 'split';
  const [, major, minor, patch] = version.map(Number);
  return major > 1 || (major === 1 && (minor > 13 || (minor === 13 && patch >= 68))) ? 'split' : 'legacy';
};

export const resolveSpotDetectionSetup = (
  setup?: ModelUserPreferences,
  mode: RnaCountMode = setup?.rnaCountMode ?? 'split'
) => {
  const compartments = setup?.segmentationCompartments ?? [...DEFAULT_SPOT_DETECTION_COMPARTMENTS];
  const dilations = setup?.segmentationDilationsUm ?? [...DEFAULT_SPOT_DETECTION_DILATIONS_UM];
  // Interpret older expanded Nuclear settings as combined counts for new runs without changing stored configurations.
  const legacyExpansion =
    mode === 'split' &&
    compartments.length === 1 &&
    compartments[0] === cellSegmentationGeometryValues.nuclear &&
    dilations.some((dilation) => dilation > 0);
  return {
    ...setup,
    rnaCountMode: mode,
    segmentationCompartments:
      mode === 'legacy'
        ? [cellSegmentationGeometryValues.nuclear]
        : legacyExpansion
        ? [...DEFAULT_SPOT_DETECTION_COMPARTMENTS]
        : compartments,
    segmentationDilationsUm: mode === 'legacy' ? [2] : dilations,
  };
};

export const getSpotDetectionScoringGeometry = (
  setup?: SpotDetectionGeometry
): { compartment: 'nuclear' | 'cell'; dilationUm?: number } => {
  const resolved = resolveSpotDetectionSetup(setup);
  if (resolved.rnaCountMode === 'legacy') return { compartment: 'nuclear', dilationUm: 2 };
  return resolved.segmentationCompartments.includes(cellSegmentationGeometryValues.cell)
    ? { compartment: 'cell', dilationUm: resolved.segmentationDilationsUm[0] }
    : { compartment: 'nuclear' };
};

// The declared RNA-probe panel for a stain: the union of every probe named across every selected
// detections-table orchestration for that stain (orchestrationMetadata already merges these across
// slides), not just whichever orchestration a single slide happens to use. A probe missing from one
// slide's own artifact must still get its bins, so this must never be scoped to one orchestration.
export const getSpotDetectionProbeNamesForStain = (
  orchestrationMetadata: OrchestrationMetadata | undefined,
  stain: string
): string[] =>
  uniq(
    flatMap(orchestrationMetadata?.[stain]?.[AttributesTable.DetectionsTable], (orchestrationData, resultKey) =>
      resultKey === SPOT_DETECTION_RESULT_NAME ? map(getDetectionLabelOptions(orchestrationData), 'id') : [resultKey]
    )
  );

// A probe id is usually also a stain type id; fall back to a human-friendly form of the raw id
// (e.g. rna_prob1 -> Rna Prob1) only when it isn't a known stain type.
export const getProbeDisplayName = (
  probeId: string | undefined,
  stainTypeIdToDisplayName: (stainId: string) => string
): string => {
  if (!probeId) {
    return '';
  }
  const displayName = stainTypeIdToDisplayName(probeId);
  return displayName === probeId ? startCase(probeId) : displayName;
};

export const getSpotDetectionCountBinsFromHScoreThresholds = (thresholds: number[]): SpotDetectionCountBin[] =>
  map(thresholds, (minCount, index) => ({
    minCount,
    maxCount: index < thresholds.length - 1 ? thresholds[index + 1] - 1 : undefined,
  }));

export const DEFAULT_SPOT_DETECTION_COUNT_BINS: SpotDetectionCountBin[] = getSpotDetectionCountBinsFromHScoreThresholds(
  DEFAULT_SPOT_DETECTION_H_SCORE_THRESHOLDS
);

export const getSpotDetectionHScoreThresholdError = (thresholds: Array<number | string>): string | undefined => {
  const parsedThresholds = map(thresholds, Number);
  if (parsedThresholds.length === 0) {
    return 'Add at least one positive score bin.';
  }
  if (parsedThresholds.some((threshold) => !Number.isInteger(threshold))) {
    return 'Bin thresholds must be whole numbers.';
  }
  if (parsedThresholds.some((threshold) => threshold < 1)) {
    return 'H-score thresholds must be at least 1 spot.';
  }
  if (parsedThresholds.some((threshold, index) => index > 0 && threshold <= parsedThresholds[index - 1])) {
    return 'Each score bin must start above the previous bin.';
  }
  return undefined;
};

export const getSpotDetectionHScoreThresholds = (
  countBins?: Array<SpotDetectionCountBin | null | undefined>
): number[] => {
  const thresholds = uniq(
    compact(
      map(countBins, (countBin) => {
        const minCount = Number(countBin?.minCount);
        return Number.isInteger(minCount) && minCount >= 1 ? minCount : undefined;
      })
    )
  ).sort((left, right) => left - right);

  return getSpotDetectionHScoreThresholdError(thresholds) ? [...DEFAULT_SPOT_DETECTION_H_SCORE_THRESHOLDS] : thresholds;
};

export const SPOT_DETECTION_COMPARTMENT_OPTIONS = [
  { value: cellSegmentationGeometryValues.nuclear, label: 'Nuclear' },
  { value: cellSegmentationGeometryValues.cell, label: 'Whole cell' },
];

// Algo estimates whole-cell contours from nuclei when membrane segmentation is absent.
export const getSpotDetectionCompartmentOptions = (
  _availableGeometries?: Array<string | null | undefined>
): Array<{ value: CellSegmentationGeometryValue; label: string }> => SPOT_DETECTION_COMPARTMENT_OPTIONS;

export const normalizeSpotDetectionCompartments = (
  compartments?: Array<string | null | undefined>,
  availableGeometries?: Array<string | null | undefined>
): CellSegmentationGeometryValue[] => {
  const availableOptions = getSpotDetectionCompartmentOptions(availableGeometries);
  const availableValues = map(availableOptions, 'value');
  const selectedValues = filter(availableValues, (value) => (compartments || []).includes(value));

  return isEmpty(selectedValues) ? [...DEFAULT_SPOT_DETECTION_COMPARTMENTS] : selectedValues;
};

export const getSpotDetectionGeometryError = (setup?: SpotDetectionGeometry): string | undefined => {
  const resolved = resolveSpotDetectionSetup(setup);
  const compartments = resolved.segmentationCompartments;
  const dilations = resolved.segmentationDilationsUm;
  if (
    compartments.length === 0 ||
    new Set(compartments).size !== compartments.length ||
    compartments.some(
      (compartment) => !SPOT_DETECTION_COMPARTMENT_OPTIONS.some((option) => option.value === compartment)
    )
  ) {
    return 'Select Nuclear or Whole cell.';
  }
  if (!compartments.includes(cellSegmentationGeometryValues.cell) && resolved.rnaCountMode !== 'legacy') {
    return undefined;
  }
  if (
    dilations.length === 0 ||
    dilations.some(
      (dilation) => !Number.isInteger(dilation) || dilation < 0 || dilation > MAX_SPOT_DETECTION_DILATION_UM
    )
  ) {
    return `Enter whole-number dilations from 0 to ${MAX_SPOT_DETECTION_DILATION_UM} µm.`;
  }
  return undefined;
};

export const getSelectedCellSegmentationGeometries = (
  models?: Array<
    { meta?: { cellSegmentationGeometries?: string[]; cell_segmentation_geometries?: string[] } } | null | undefined
  >
): string[] =>
  uniq(compact(flatMap(models, (model) => getCellSegmentationGeometries(model?.meta ? { meta: model.meta } : null))));

// Model capability does not prove that a run measured membranes. Keep this distinction for the
// assignment explanation even though algo can estimate whole-cell contours from nuclei.
export const hasConfirmedWholeCellSegmentation = (jobs?: Array<{ params?: unknown } | null | undefined>): boolean =>
  some(jobs, (job) => {
    const params = job?.params as { membranalChannelNames?: string[]; membranal_channel_names?: string[] } | undefined;
    return !isEmpty(params?.membranalChannelNames ?? params?.membranal_channel_names);
  });

export const getConfirmedCellSegmentationGeometries = (
  modelDeclaredGeometries: string[],
  jobs?: Array<{ params?: unknown } | null | undefined>
): string[] =>
  hasConfirmedWholeCellSegmentation(jobs)
    ? modelDeclaredGeometries
    : filter(modelDeclaredGeometries, (geometry) => geometry === cellSegmentationGeometryValues.nuclear);

export const getDefaultSpotDetectionSetup = (): Pick<
  ModelUserPreferences,
  'segmentationCompartments' | 'segmentationDilationsUm' | 'countBins' | 'positivityThresholdScore' | 'rnaCountMode'
> => ({
  segmentationCompartments: [...DEFAULT_SPOT_DETECTION_COMPARTMENTS],
  segmentationDilationsUm: [...DEFAULT_SPOT_DETECTION_DILATIONS_UM],
  countBins: DEFAULT_SPOT_DETECTION_COUNT_BINS.map((bin) => ({ ...bin })),
  positivityThresholdScore: DEFAULT_SPOT_DETECTION_POSITIVITY_THRESHOLD_SCORE,
});

export const normalizeSpotDetectionDilationsUm = (dilations?: Array<number | string | null>): number[] => {
  const uniqueDilations = new Set<number>();

  map(dilations, (dilation) => {
    const parsedDilation = typeof dilation === 'number' ? dilation : Number(dilation);
    if (!Number.isInteger(parsedDilation) || parsedDilation < 0 || parsedDilation > MAX_SPOT_DETECTION_DILATION_UM) {
      return;
    }
    uniqueDilations.add(parsedDilation);
  });

  return Array.from(uniqueDilations).sort((left, right) => left - right);
};

export const normalizeSpotDetectionCountBins = (
  countBins?: Array<SpotDetectionCountBin | null | undefined>
): SpotDetectionCountBin[] =>
  getSpotDetectionCountBinsFromHScoreThresholds(getSpotDetectionHScoreThresholds(countBins));

// Clamps a requested positivity-threshold score to a valid positive score for the current bins,
// falling back to Score 1 when unset or when the bins have since shrunk past the requested score.
export const normalizeSpotDetectionPositivityThresholdScore = (
  score: number | undefined,
  countBins: SpotDetectionCountBin[]
): number =>
  Number.isInteger(score) && (score as number) >= 1 && (score as number) <= countBins.length
    ? (score as number)
    : DEFAULT_SPOT_DETECTION_POSITIVITY_THRESHOLD_SCORE;

// The score the user marks as the positivity threshold indicator. Defaults to Score 1 (the first
// positive score), matching the assumption that any positive spot count counts as "positive".
export const getSpotDetectionPositivityThresholdMinCount = (
  setup?: Pick<ModelUserPreferences, 'countBins' | 'positivityThresholdScore'>
): number => {
  const countBins = normalizeSpotDetectionCountBins(setup?.countBins);
  const score = normalizeSpotDetectionPositivityThresholdScore(setup?.positivityThresholdScore, countBins);
  return countBins[score - 1]?.minCount ?? 0;
};

export const getSpotDetectionCountBinLabel = (countBin: SpotDetectionCountBin): string => {
  if (countBin.minCount !== undefined && countBin.maxCount === undefined) {
    return `${countBin.minCount}+`;
  }
  if (countBin.minCount !== undefined && countBin.maxCount !== undefined && countBin.minCount === countBin.maxCount) {
    return String(countBin.minCount);
  }
  if (countBin.minCount !== undefined && countBin.maxCount !== undefined) {
    return `[${countBin.minCount}, ${countBin.maxCount}]`;
  }
  if (countBin.maxCount !== undefined) {
    return `<= ${countBin.maxCount}`;
  }
  return 'Count bin';
};

export const getSpotDetectionCountBinSpotsLabel = (countBin: SpotDetectionCountBin): string =>
  `${getSpotDetectionCountBinLabel(countBin)} ${countBin.minCount === 1 && countBin.maxCount === 1 ? 'spot' : 'spots'}`;

const spotDetectionCompartmentColumnLabels: Record<string, string> = {
  [cellSegmentationGeometryValues.nuclear]: 'nuclear',
  [cellSegmentationGeometryValues.cell]: 'whole_cell',
};

export interface SpotDetectionCountColumn {
  columnName: string;
  displayLabel: string;
}

export interface SpotDetectionCountBinCellDefinition extends SpotDetectionCountColumn {
  id: string;
  label: string;
  condition: AutomaticCondition;
}

export const getSpotDetectionCountColumns = (
  probeName: string,
  setup?: SpotDetectionGeometry,
  probeDisplayName: string = probeName
): SpotDetectionCountColumn[] => {
  const resolved = resolveSpotDetectionSetup(setup);
  const scoring = getSpotDetectionScoringGeometry(resolved);
  const compartments = [
    scoring.compartment === 'cell' ? cellSegmentationGeometryValues.cell : cellSegmentationGeometryValues.nuclear,
  ];
  const dilations =
    scoring.compartment === 'nuclear'
      ? [resolved.rnaCountMode === 'legacy' ? 2 : 0]
      : normalizeSpotDetectionDilationsUm(resolved.segmentationDilationsUm);

  return flatMap(compartments, (compartment) =>
    map(dilations, (dilation) => {
      const compartmentColumnLabel = spotDetectionCompartmentColumnLabels[compartment] ?? compartment;
      const compartmentDisplayLabel =
        SPOT_DETECTION_COMPARTMENT_OPTIONS.find((option) => option.value === compartment)?.label ?? compartment;

      return {
        columnName: `${probeName}.counts_in_segmentation.${compartmentColumnLabel}.dilation.${dilation}um`,
        displayLabel: `${probeDisplayName} · ${compartmentDisplayLabel} · ${dilation} µm`,
      };
    })
  );
};

const getSpotDetectionCountBinCondition = (
  mappingFilter: string,
  countBin: SpotDetectionCountBin,
  displayValue: string
): AutomaticCondition => {
  const conditions: AutomaticCondition[] = [];

  if (countBin.minCount !== undefined) {
    conditions.push({
      conditionType: ConditionType.ATTRIBUTES_TABLE,
      mappingFilter,
      filterAction: countBin.minCount === countBin.maxCount ? '$eq' : '$gte',
      toFilter: countBin.minCount,
      displayValue,
    });
  }
  if (countBin.maxCount !== undefined && countBin.maxCount !== countBin.minCount) {
    conditions.push({
      conditionType: ConditionType.ATTRIBUTES_TABLE,
      mappingFilter,
      filterAction: '$lte',
      toFilter: countBin.maxCount,
      displayValue,
    });
  }

  return conditions.length === 1
    ? conditions[0]
    : {
        operator: ConditionOperator.AND,
        operands: conditions,
      };
};

export const getSpotDetectionCountBinCellDefinitions = (
  probeName: string,
  setup?: Pick<ModelUserPreferences, 'segmentationCompartments' | 'segmentationDilationsUm' | 'countBins'>,
  probeDisplayName: string = probeName
): SpotDetectionCountBinCellDefinition[] =>
  flatMap(getSpotDetectionCountColumns(probeName, setup, probeDisplayName), (column) =>
    map(normalizeSpotDetectionCountBins(setup?.countBins), (countBin) => {
      const label = `${column.displayLabel} · ${getSpotDetectionCountBinSpotsLabel(countBin)}`;
      const mappingFilter = `cell.${column.columnName}`;

      return {
        ...column,
        id: `spot-count:${column.columnName}:${countBin.minCount ?? ''}:${countBin.maxCount ?? ''}`,
        label,
        condition: getSpotDetectionCountBinCondition(mappingFilter, countBin, label),
      };
    })
  );

export const SPOT_DETECTION_ASSIGNMENT_HELP =
  'Whole-cell counts use measured membranes when available, otherwise estimated contours. Nuclear counts use the undilated nucleus.';

export const getSpotDetectionSelectedGeometryLabel = (
  compartments?: Array<string | null | undefined>,
  availableGeometries?: Array<string | null | undefined>
): string => {
  const selectedCompartments = normalizeSpotDetectionCompartments(compartments, availableGeometries);
  const selectedLabels = compact(
    map(getSpotDetectionCompartmentOptions(availableGeometries), (option) =>
      selectedCompartments.includes(option.value) ? option.label : undefined
    )
  );

  return isEmpty(selectedLabels) ? 'Select a geometry' : selectedLabels.join(', ');
};

export const getSpotDetectionDilationChipLabel = (
  dilation: number,
  compartments?: Array<string | null | undefined>,
  availableGeometries?: Array<string | null | undefined>
): string => `${getSpotDetectionSelectedGeometryLabel(compartments, availableGeometries)}: ${dilation} µm`;

export const getSpotDetectionSetupSummary = (
  setup?: ModelUserPreferences,
  availableGeometries?: Array<string | null | undefined>
): string => {
  const normalizedSetup = {
    ...resolveSpotDetectionSetup(setup),
    countBins: isEmpty(setup?.countBins)
      ? DEFAULT_SPOT_DETECTION_COUNT_BINS.map((bin) => ({ ...bin }))
      : normalizeSpotDetectionCountBins(setup?.countBins),
  };
  const geometryLabel = getSpotDetectionSelectedGeometryLabel(
    normalizedSetup.segmentationCompartments,
    availableGeometries
  );
  const expansionLabel =
    normalizedSetup.rnaCountMode === 'legacy' ||
    normalizedSetup.segmentationCompartments.includes(cellSegmentationGeometryValues.cell)
      ? ` · expansion ${map(normalizedSetup.segmentationDilationsUm, (dilation) => `${dilation} µm`).join(', ')}`
      : '';
  const countLabel = map(normalizedSetup.countBins, getSpotDetectionCountBinLabel).join(', ');

  return `${geometryLabel}${expansionLabel} · counts ${countLabel}`;
};

// Converts the H-score bins editor's positive score ranges (Score 1..N) into the exact
// `bin_points_table_column` contract: half-open [minValue, maxValue) ranges, where the tool itself
// extends the last bin's max to include the true maximum. Score 0 (implicit, not part of countBins)
// covers every count below the first positive threshold.
export const getSpotDetectionMapValuesToBins = (
  countBins: SpotDetectionCountBin[]
): Array<{ bin: number; minValue?: number; maxValue?: number }> => {
  if (isEmpty(countBins)) {
    return [];
  }

  const scoreZeroMax = countBins[0]?.minCount;
  return [
    { bin: 0, minValue: 0, maxValue: scoreZeroMax },
    ...map(countBins, (countBin, index) => ({
      bin: index + 1,
      minValue: countBin.minCount,
      maxValue: countBin.maxCount === undefined ? undefined : countBin.maxCount + 1,
    })),
  ];
};

// Reverses getSpotDetectionMapValuesToBins: drops the implicit Score-0 entry and converts each
// half-open [minValue, maxValue) range back to the editor's inclusive {minCount, maxCount} shape.
const getCountBinsFromMapValuesToBins = (
  mapValuesToBins?: Array<{ bin?: string | number; minValue?: number; maxValue?: number }>
): SpotDetectionCountBin[] =>
  compact(
    map(mapValuesToBins, ({ bin, minValue, maxValue }) => {
      if (Number(bin) === 0) {
        return undefined;
      }
      const minCount = minValue === undefined || minValue === null ? undefined : Number(minValue);
      const maxCount = maxValue === undefined || maxValue === null ? undefined : Number(maxValue) - 1;

      return minCount === undefined && maxCount === undefined ? undefined : { minCount, maxCount };
    })
  );

// A probe with its own countBins override uses that; every other probe uses the general countBins.
export const getEffectiveSpotDetectionCountBins = (
  generalCountBins: SpotDetectionCountBin[] | undefined,
  probeName: string,
  perProbeCountBins?: Record<string, SpotDetectionCountBin[]>
): SpotDetectionCountBin[] =>
  normalizeSpotDetectionCountBins(
    isEmpty(perProbeCountBins?.[probeName]) ? generalCountBins : perProbeCountBins?.[probeName]
  );

export interface SpotDetectionBinningDefinition {
  mapValuesToBins: Array<{ bin: number; minValue?: number; maxValue?: number }>;
  columnsToBin?: string[];
}

// Builds the payload's binning definitions for one detections-table Input. With no probe overrides,
// this stays a single definition covering every generated count column (today's general-only
// behavior, unchanged). Once any probe has a countBins override, every definition needs an explicit
// columnsToBin (the algo/backend contract requires this once any definition scopes its columns), so
// the general definition is scoped to the remaining, non-overridden probes' columns.
export const getSpotDetectionBinningDefinitions = (
  setup: Pick<ModelUserPreferences, 'countBins' | 'segmentationCompartments' | 'segmentationDilationsUm'>,
  probeNames: string[],
  perProbeCountBins?: Record<string, SpotDetectionCountBin[]>
): SpotDetectionBinningDefinition[] => {
  const overriddenProbeNames = filter(
    keys(perProbeCountBins),
    (probeName) => probeNames.includes(probeName) && !isEmpty(perProbeCountBins?.[probeName])
  );

  if (isEmpty(probeNames) || isEmpty(overriddenProbeNames)) {
    return [{ mapValuesToBins: getSpotDetectionMapValuesToBins(normalizeSpotDetectionCountBins(setup.countBins)) }];
  }

  const getColumnNamesForProbe = (probeName: string) =>
    map(getSpotDetectionCountColumns(probeName, setup), 'columnName');
  const defaultProbeNames = filter(probeNames, (probeName) => !overriddenProbeNames.includes(probeName));
  const definitions: SpotDetectionBinningDefinition[] = [];

  if (!isEmpty(defaultProbeNames)) {
    definitions.push({
      mapValuesToBins: getSpotDetectionMapValuesToBins(normalizeSpotDetectionCountBins(setup.countBins)),
      columnsToBin: flatMap(defaultProbeNames, getColumnNamesForProbe),
    });
  }
  forEach(overriddenProbeNames, (probeName) => {
    definitions.push({
      mapValuesToBins: getSpotDetectionMapValuesToBins(
        getEffectiveSpotDetectionCountBins(setup.countBins, probeName, perProbeCountBins)
      ),
      columnsToBin: getColumnNamesForProbe(probeName),
    });
  });

  return definitions;
};

export interface SpotDetectionSetupRestoreResult
  extends Pick<ModelUserPreferences, 'segmentationCompartments' | 'segmentationDilationsUm' | 'countBins'> {
  // Restored per-probe overrides, keyed by probe name. Callers store each entry under its own
  // modelPreferencesByModelId[probeName], mirroring how per-probe overrides are read elsewhere
  // (see getSpotDetectionThresholdOptions and friends in usePostProcessingActions.tsx).
  perProbeCountBins?: Record<string, SpotDetectionCountBin[]>;
}

export const getSpotDetectionSetupFromInput = (
  input?: Pick<Input, 'segmentationCompartments' | 'segmentationDilationsUm' | 'countBins' | 'binning'>
): SpotDetectionSetupRestoreResult => {
  const defaults = getDefaultSpotDetectionSetup();
  const binning = input?.binning as
    | {
        mapValuesToBins?: Array<{ bin?: string | number; minValue?: number; maxValue?: number }>;
        columnsToBin?: string[];
      }
    | Array<{
        mapValuesToBins?: Array<{ bin?: string | number; minValue?: number; maxValue?: number }>;
        columnsToBin?: string[];
      }>
    | undefined;
  const definitions = Array.isArray(binning) ? binning : binning ? [binning] : [];
  // getSpotDetectionBinningDefinitions always places the shared/general definition first (when one
  // exists) and every subsequent definition is a per-probe override; restore mirrors that construction
  // order instead of guessing from columnsToBin cardinality, which is ambiguous whenever exactly one
  // probe is left on the general definition (indistinguishable from a single-probe override by shape).
  const getProbeNamesFromColumns = (columns?: string[]) => uniq(map(columns, (column) => column.split('.')[0]));

  let restoredGeneralCountBins: SpotDetectionCountBin[] | undefined;
  const perProbeCountBins: Record<string, SpotDetectionCountBin[]> = {};

  forEach(definitions, (definition, index) => {
    const bins = getCountBinsFromMapValuesToBins(definition?.mapValuesToBins);
    if (isEmpty(bins)) {
      return;
    }
    if (index === 0) {
      restoredGeneralCountBins = bins;
      return;
    }
    forEach(getProbeNamesFromColumns(definition?.columnsToBin), (probeName) => {
      perProbeCountBins[probeName] = bins;
    });
  });

  const restoredCountBins = isEmpty(restoredGeneralCountBins) ? input?.countBins : restoredGeneralCountBins;

  return {
    segmentationCompartments: isEmpty(input?.segmentationCompartments)
      ? [cellSegmentationGeometryValues.nuclear]
      : input?.segmentationCompartments,
    segmentationDilationsUm: isEmpty(input?.segmentationDilationsUm)
      ? input?.segmentationCompartments?.length === 1 &&
        input.segmentationCompartments[0] === cellSegmentationGeometryValues.nuclear
        ? []
        : defaults.segmentationDilationsUm
      : normalizeSpotDetectionDilationsUm(input?.segmentationDilationsUm),
    countBins: isEmpty(restoredCountBins) ? defaults.countBins : normalizeSpotDetectionCountBins(restoredCountBins),
    perProbeCountBins: isEmpty(perProbeCountBins) ? undefined : perProbeCountBins,
  };
};

export const hasSpotDetectionSetup = (
  input?: Pick<Input, 'segmentationCompartments' | 'segmentationDilationsUm' | 'countBins' | 'binning'>
): boolean =>
  Boolean(
    !isEmpty(input?.segmentationCompartments) ||
      !isEmpty(input?.segmentationDilationsUm) ||
      !isEmpty(input?.countBins) ||
      !isEmpty(input?.binning)
  );

export const applySpotDetectionSetupToInput = (
  input: Input,
  preferences?: ModelUserPreferences,
  probeNames: string[] = [],
  perProbeCountBins?: Record<string, SpotDetectionCountBin[]>
): Input => {
  const defaults = getDefaultSpotDetectionSetup();
  const setup = resolveSpotDetectionSetup({
    ...preferences,
    segmentationCompartments: preferences?.segmentationCompartments ?? defaults.segmentationCompartments,
    segmentationDilationsUm: preferences?.segmentationDilationsUm ?? defaults.segmentationDilationsUm,
  });
  input.segmentationCompartments = setup.segmentationCompartments;
  if (setup.rnaCountMode === 'legacy' || setup.segmentationCompartments.includes(cellSegmentationGeometryValues.cell)) {
    input.segmentationDilationsUm = setup.segmentationDilationsUm;
  } else {
    delete input.segmentationDilationsUm;
  }
  // count_bins has no algo consumer; the binned count columns instead come from binning.mapValuesToBins,
  // which bin_instance_count_columns automatically scopes to every generated count column (all probes
  // by default, or the exact probes named in columnsToBin once a probe has its own countBins override).
  const definitions = getSpotDetectionBinningDefinitions(setup, probeNames, perProbeCountBins);
  input.binning = definitions.length === 1 ? definitions[0] : definitions;
  return input;
};

export const resolveSpotDetectionSetupPreference = (params: {
  resultKey: string;
  resultInput: Input;
}): {
  preferenceKey: string;
  preferenceValue: Pick<ModelUserPreferences, 'segmentationCompartments' | 'segmentationDilationsUm' | 'countBins'>;
  perProbeCountBins?: Record<string, SpotDetectionCountBin[]>;
} | null => {
  const { resultKey, resultInput } = params;
  if (!hasSpotDetectionSetup(resultInput)) {
    return null;
  }

  const { perProbeCountBins, ...preferenceValue } = getSpotDetectionSetupFromInput(resultInput);
  return {
    preferenceKey: resultKey,
    preferenceValue,
    perProbeCountBins,
  };
};
